import { collection, collectionGroup, query, where, getDocs, getFirestore, limit } from "firebase/firestore";
import firebaseApp from "../firebase/credenciales";
import { format, startOfMonth, endOfMonth, isWithinInterval, differenceInDays } from "date-fns";
import { registrarLecturas } from "./consumoLecturas";

const db = getFirestore(firebaseApp);

/**
 * Obtiene indicadores consolidados de asistencia para el Dashboard.
 *
 * VERSIÓN OPTIMIZADA (Fase 6.1):
 * - ANTES: 2 queries por cada empleado activo (N+1) → ~3M reads para M empleados
 * - DESPUÉS: 1 query collectionGroup("registros") + 1 query collectionGroup("ausencias")
 *            → ~2-3 reads + documentos del resultado
 *
 * Los indicadores calculados son idénticos a la versión anterior.
 */
export async function obtenerIndicadoresAsistencia({ rucEmpresa, year, month }) {
  const ORIGEN = "dashboard:indicadores";

  // ─── 1. Obtener empleados activos ──────────────────────────────────────────
  const usersRef = collection(db, "users");
  const usersQuery = query(
    usersRef,
    where("rucEmpresa", "==", rucEmpresa),
    where("activo", "==", true),
    limit(500)
  );
  const usersSnapshot = await getDocs(usersQuery);
  registrarLecturas({ users: usersSnapshot.size }, { origen: ORIGEN });
  const empleados = [];
  usersSnapshot.forEach(doc => empleados.push(doc.data()));
  const empleadosDni = empleados.map(e => e.dni);

  // ─── 2. Fechas del mes ─────────────────────────────────────────────────────
  const daysInMonth = new Date(year, month, 0).getDate();
  const startDate = startOfMonth(new Date(year, month - 1));
  const endDate = endOfMonth(new Date(year, month - 1));

  const fechaInicio = `${year}-${String(month).padStart(2, "0")}-01`;
  const fechaFin = `${year}-${String(month).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;

  // ─── 3. Obtener TODAS las asistencias de la empresa en una sola query ─────
  // ANTES: 2 queries por empleado (asistencia + ausencias) = ~2M reads
  // DESPUÉS: 1 query collectionGroup = ~1 read (Firestore internamente pagina)
  const attendanceRecords = [];
  try {
    const asistenciaQuery = query(
      collectionGroup(db, "registros"),
      where("ruc", "==", rucEmpresa),
      where("fechaJornada", ">=", fechaInicio),
      where("fechaJornada", "<=", fechaFin)
    );
    const asistenciaSnapshot = await getDocs(asistenciaQuery);
    registrarLecturas({ registros: asistenciaSnapshot.size }, { origen: ORIGEN });
    asistenciaSnapshot.forEach(doc => {
      attendanceRecords.push({ id: doc.id, ...doc.data() });
    });
  } catch (err) {
    console.warn("obtenerIndicadoresAsistencia: Error en query collectionGroup registros:", err.message);
  }

  // ─── 4. Obtener TODAS las ausencias de la empresa en una sola query ───────
  // ANTES: 1 query por empleado = ~M reads
  // DESPUÉS: 1 query collectionGroup = ~1 read
  // Nota: Firestore no permite where("fechaInicio") AND where("fechaFin") en la misma query
  // porque son desigualdades en campos diferentes. Filtramos fechaFin en cliente.
  const absenceRecords = [];
  try {
    const ausenciasQuery = query(
      collectionGroup(db, "ausencias"),
      where("ruc", "==", rucEmpresa),
      where("fechaInicio", "<=", endDate)
    );
    const ausenciasSnapshot = await getDocs(ausenciasQuery);
    registrarLecturas({ ausencias: ausenciasSnapshot.size }, { origen: ORIGEN });
    ausenciasSnapshot.forEach(doc => {
      const data = doc.data();
      // Filtrar por solapamiento con el mes requerido
      if (data.fechaInicio && data.fechaFin) {
        const absenceStart = data.fechaInicio.toDate();
        const absenceEnd = data.fechaFin.toDate();
        if (
          isWithinInterval(startDate, { start: absenceStart, end: absenceEnd }) ||
          isWithinInterval(endDate, { start: absenceStart, end: absenceEnd }) ||
          isWithinInterval(absenceStart, { start: startDate, end: endDate })
        ) {
          // Extraer DNI del path del documento: ausencias/{dni}/ausencias/{docId}
          const docPath = doc.ref.path;
          const pathParts = docPath.split("/");
          const dni = pathParts[1]; // ausencias / {dni} / ausencias / {docId}
          absenceRecords.push({ dni, ...data });
        }
      }
    });
  } catch (err) {
    console.warn("obtenerIndicadoresAsistencia: Error en query collectionGroup ausencias:", err.message);
  }

  // ─── 5. KPIs (misma lógica que antes) ─────────────────────────────────────
  const uniqueEmployees = new Set(attendanceRecords.map(r => r.dni));
  absenceRecords.forEach(r => { if (r.dni) uniqueEmployees.add(r.dni); });

  let laborables = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month - 1, d);
    const dayOfWeek = date.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) laborables++;
  }
  const workingDays = laborables * uniqueEmployees.size;

  const completosPorEmpleado = {};
  attendanceRecords.forEach(record => {
    if (!record.dni || !record.fechaJornada) return;
    const key = record.dni + "-" + (typeof record.fechaJornada === "string"
      ? record.fechaJornada
      : format(record.fechaJornada.toDate(), "yyyy-MM-dd"));
    if (!completosPorEmpleado[key])
      completosPorEmpleado[key] = { ingreso: false, salida: false };
    if (record.tipo === "Ingreso") completosPorEmpleado[key].ingreso = true;
    if (record.tipo === "Salida") completosPorEmpleado[key].salida = true;
  });
  const diasCompletos = Object.values(completosPorEmpleado).filter(
    v => v.ingreso && v.salida
  ).length;

  const attendanceRate = workingDays > 0 ? (diasCompletos / workingDays) * 100 : 0;

  const hoy = new Date();
  const esMesActual = Number(year) === hoy.getFullYear() && Number(month) === hoy.getMonth() + 1;
  let laborablesHastaHoy = 0;
  if (esMesActual) {
    for (let d = 1; d <= hoy.getDate(); d++) {
      const date = new Date(year, month - 1, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) laborablesHastaHoy++;
    }
  } else {
    laborablesHastaHoy = laborables;
  }
  const objetivoAFecha = workingDays > 0
    ? ((laborablesHastaHoy * uniqueEmployees.size) / workingDays) * 100
    : 0;

  let absenceDays = 0;
  absenceRecords.forEach(absence => {
    if (!absence.fechaInicio || !absence.fechaFin) return;
    const startDateAbs = absence.fechaInicio.toDate();
    const endDateAbs = absence.fechaFin.toDate();
    const rangeStart = new Date(Math.max(startDateAbs.getTime(), new Date(year, month - 1, 1).getTime()));
    const rangeEnd = new Date(Math.min(endDateAbs.getTime(), new Date(year, month, 0).getTime()));
    const daysInRange = differenceInDays(rangeEnd, rangeStart) + 1;
    absenceDays += daysInRange;
  });
  const absenceRate = workingDays > 0 ? (absenceDays / workingDays) * 100 : 0;

  const startHour = 9, startMinute = 0;
  const lateArrivals = attendanceRecords.filter(record => {
    if (record.tipo !== "Ingreso") return false;
    const timeStr = record.fechaMarcacion;
    if (!timeStr) return false;
    try {
      const [hour, minute] = timeStr.split(":").map(Number);
      return hour > startHour || (hour === startHour && minute > startMinute);
    } catch {
      return false;
    }
  }).length;

  const attendanceByDay = {};
  attendanceRecords.forEach(record => {
    if (!record.fechaJornada) return;
    const fecha = typeof record.fechaJornada === "string"
      ? record.fechaJornada
      : format(record.fechaJornada.toDate(), "yyyy-MM-dd");
    if (record.tipo === "Ingreso") {
      attendanceByDay[fecha] = (attendanceByDay[fecha] || 0) + 1;
    }
  });

  let perfectos = 0;
  for (const emp of empleadosDni) {
    let completos = 0;
    for (let d = 1; d <= (esMesActual ? hoy.getDate() : daysInMonth); d++) {
      const date = new Date(year, month - 1, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const fecha = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const key = emp + "-" + fecha;
        if (
          completosPorEmpleado[key] &&
          completosPorEmpleado[key].ingreso &&
          completosPorEmpleado[key].salida
        ) {
          completos++;
        }
      }
    }
    if (completos === laborablesHastaHoy && laborablesHastaHoy > 0) perfectos++;
  }
  const porcentajePerfectos = empleadosDni.length > 0
    ? (perfectos / empleadosDni.length) * 100
    : 0;

  const labels = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`);
  const dailyAttendanceData = labels.map(day => {
    const date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return attendanceByDay[date] || 0;
  });

  return {
    totalEmployees: empleadosDni.length,
    attendanceRate,
    absenceRate,
    lateArrivals,
    porcentajePerfectos,
    attendanceByDay,
    objetivoAFecha,
    laborables,
    dailyAttendanceChart: {
      labels,
      datasets: [
        {
          label: "Asistencias diarias",
          data: dailyAttendanceData,
          fill: false,
          borderColor: "#2196f3",
          tension: 0.1,
        },
      ],
    },
  };
}
